import React, { useEffect, useMemo, useState } from "react";
import "./TraditionalWestern.css";
import { Link } from "react-router-dom";
import { getSupabaseProductsByCategories } from "../lib/productService";
import { supabase } from "../lib/supabase";
import { useCartWishlist } from "../contexts/CartWishlistContext";
import Pagination from "../components/Pagination";

const formatINR = (amount) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(amount);

const PER_PAGE = 12;

// Set to true when client adds Traditional & Western products via Dashboard
const SHOW_TRADITIONAL_PRODUCTS = true;

export default function TraditionalWestern() {
  const { addToCart, incQty, decQty, getQty, toggleWish, isWished } = useCartWishlist();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (SHOW_TRADITIONAL_PRODUCTS) {
      supabase.from("categories")
        .select("*")
        .then(({ data: allCats }) => {
          const validCatNames = new Set();
          const subCategoryNames = new Set();
          const subSubMap = {};
          
          if (allCats) {
            const mainTrad = allCats.find(c => c.name === "Traditional & Western Wear");
            if (mainTrad) {
              const subCats = allCats.filter(c => c.parent_id === mainTrad.id);
              subCats.forEach(sub => {
                validCatNames.add(sub.name);
                subCategoryNames.add(sub.name);
                subSubMap[sub.name] = [];
                
                const subSubCats = allCats.filter(c => c.parent_id === sub.id);
                subSubCats.forEach(subSub => {
                  validCatNames.add(subSub.name);
                  subSubMap[sub.name].push(subSub.name);
                });
              });
            }
            
            allCats.forEach(c => {
              if (c.description === 'Clothing Sub-category' && !subCategoryNames.has(c.name)) {
                validCatNames.add(c.name);
                subCategoryNames.add(c.name);
                subSubMap[c.name] = [];
                
                const subSubCats = allCats.filter(child => child.parent_id === c.id);
                subSubCats.forEach(subSub => {
                  validCatNames.add(subSub.name);
                  subSubMap[c.name].push(subSub.name);
                });
              }
            });
          }
          
          const catList = Array.from(subCategoryNames).sort((a, b) => a.localeCompare(b));
          setCategories(catList);
          setCategoryTree(subSubMap);
          
          return getSupabaseProductsByCategories([...Array.from(validCatNames), "Traditional & Western Wear"]);
        })
        .then((list) => {
          const sortedList = list.sort((a, b) => (a.category || "Other").localeCompare(b.category || "Other"));
          setProducts(
            sortedList.map((p) => ({
              id: p.id,
              name: p.title,
              price: p.offerPrice,
              img: p.images?.[0],
              link: `/product/${p.slug || p.id}`,
              isBestseller: p.isBestseller,
              category: p.category || "Traditional & Western Wear",
            }))
          );
        })
        .finally(() => setLoading(false));
    } else {
      setProducts([]);
      setLoading(false);
    }
  }, []);

  const filteredProducts = useMemo(() => {
    if (activeTab === "all") return products;
    const allowedCategories = new Set([activeTab, ...(categoryTree[activeTab] || [])]);
    return products.filter(p => allowedCategories.has(p.category));
  }, [products, activeTab, categoryTree]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filteredProducts.slice(start, start + PER_PAGE);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / PER_PAGE) || 1;

  const paginatedItemsByCategory = useMemo(() => {
    const groups = [];
    let lastCat = null;
    paginatedProducts.forEach((item) => {
      const cat = item.category === "Traditional & Western Wear" ? "From Our Collection" : item.category;
      if (cat !== lastCat) {
        groups.push({ category: cat, items: [] });
        lastCat = cat;
      }
      groups[groups.length - 1].items.push(item);
    });
    return groups;
  }, [paginatedProducts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const toggleWishlist = (item) => {
    toggleWish({ ...item, link: item.link || `/${item.id}` });
  };

  const renderCard = (item) => {
    const qty = getQty(item.id);
    const wished = isWished(item.id);
    return (
      <Link key={item.id} to={item.link} className="twCardLink">
        <article className="twCard" tabIndex={0}>
          <div className="twMedia" style={{ "--tw-img": item.img ? `url(${item.img})` : undefined }}>
            {item.isBestseller && <div className="twRibbon">Bestseller</div>}
            <img
              className="twImg"
              src={item.img || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f3f3' width='200' height='200'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E"}
              alt={item.name}
            />
          </div>
          <div className="twInfo">
            <div className="twName">{item.name}</div>
            <div className="twMeta">
              <div className="twPrice">₹ {formatINR(item.price)}</div>
              <button
                className={`twWish ${wished ? "active" : ""}`}
                type="button"
                aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleWishlist(item);
                }}
              >
                {wished ? "♥" : "♡"}
              </button>
            </div>
            {qty === 0 ? (
              <button
                className="twAddCart"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToCart(item);
                }}
              >
                Add to Cart
              </button>
            ) : (
              <div className="twQtyWrap" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <button
                  className="twQtyBtn"
                  type="button"
                  aria-label="Decrease"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); decQty(item.id); }}
                >
                  −
                </button>
                <span className="twQtyNum">{qty}</span>
                <button
                  className="twQtyBtn"
                  type="button"
                  aria-label="Increase"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); incQty(item.id); }}
                >
                  +
                </button>
              </div>
            )}
          </div>
        </article>
      </Link>
    );
  };

  return (
    <div className="twPage">
      <section className="twTop">
        <h2 className="twBigTitle">TRADITIONAL &amp; WESTERN WEAR</h2>

        {loading ? (
          <div className="twComingSoon">
            <p className="twComingSoonNote">Loading...</p>
          </div>
        ) : (
          <div className="twSection">
            <div className="twWrap">
              <div className="twSubSection">
                {categories.length > 0 && (
                  <div className="jFilters" role="tablist" style={{ marginBottom: '40px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <button
                      className={`jFilterBtn ${activeTab === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveTab('all')}
                    >
                      All
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat}
                        className={`jFilterBtn ${activeTab === cat ? 'active' : ''}`}
                        onClick={() => setActiveTab(cat)}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}

                {paginatedProducts.length > 0 ? (
                  <>
                    {paginatedItemsByCategory.map(({ category, items }) => (
                      <div key={category} style={{ marginBottom: '40px' }}>
                        <div className="twSubHead">
                          <h4 className="twSubTitle">{category}</h4>
                          <div className="twSubLine" />
                        </div>
                        <div className="twGrid">{items.map(renderCard)}</div>
                      </div>
                    ))}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </>
                ) : (
                  <div className="twComingSoon">
                    <p className="twComingSoonTitle">Coming Soon</p>
                    <p className="twComingSoonNote">
                      We're curating products for {activeTab === 'all' ? 'this collection' : `the ${activeTab} category`}. Check back later!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
